import { CanvasContext } from "client/contexts/ResizeEditor/Canvas";
import { useThrottleFunc } from "client/hooks/utils/useThrottleFunc";
import { calculateCanvasImageDimensions } from "client/utils/calculateCanvasImageDimensions";
import { loadImage } from "client/utils/imageEdit/loadImage";
import { loadImageToCanvas } from "client/utils/imageEdit/loadImageToCanvas";
import { loadSrcToImage } from "client/utils/loadSrcToImage";
import { sleep } from "client/utils/sleep";
import { useContext, useRef, useState } from "react";
import { rotateSetRotation, rotateSetStraighten } from "store/features/flipRotate/actions";
import { flipRotateSelector } from "store/features/flipRotate/selectors/flipRotateSelector";
import { FlipRotateState } from "store/features/flipRotate/types";
import { useLoadFromParentImage } from "store/features/resizeEditor/hooks/useLoadFromParentImage";
import { useAppSelector, useAppDispatch, store } from "store/store";
import { CanvasImage, Orientation } from "store/types/crop";

const ROTATE_THROTTLE_TIME = 100; // 100ms

export const useRotateSettings = () => {
    const {
        image,
        rotate: { rotation, straighten },
    } = useAppSelector(flipRotateSelector, equalityFn);
    const dispatch = useAppDispatch();
    const { rotateRef, parentImageRef } = useContext(CanvasContext);
    const { isLoaded } = useLoadFromParentImage(rotateRef);
    const throttle = useThrottleFunc(ROTATE_THROTTLE_TIME);
    const isSlidingRef = useRef(false);
    const [isRotating, setIsRotating] = useState<"clock-wise" | "reverse-clock-wise" | "">("");

    const handleStraightenChange = async (value: number) => {
        if (isRotating) return;
        dispatch(rotateSetStraighten(value));
        isSlidingRef.current = true;

        throttle(async () => {
            if (!rotateRef?.current || !parentImageRef?.current || !image) return;
            rotateImage(
                parentImageRef.current,
                rotateRef.current,
                rotateRef.current.width,
                rotation,
                value,
                isSlidingRef.current,
            );
        });
    };

    const handleDirection = async (direction: "clock-wise" | "reverse-clock-wise") => {
        if (!isLoaded || !rotateRef?.current || !parentImageRef?.current || !image || isRotating) return;
        setIsRotating(direction);
        await sleep();
        const weight = direction === "clock-wise" ? 1 : -1;
        const value = (rotation + 90 * weight) % 360;
        dispatch(rotateSetRotation(value));

        rotateImage(
            parentImageRef.current,
            rotateRef.current,
            getMaximumWidth(image, getOrientation(value)),
            value,
            straighten,
            false,
        );
        await sleep();
        setIsRotating("");
    };

    const getMaximumWidth = (image: CanvasImage, orientation: Orientation): number => {
        return calculateCanvasImageDimensions(
            orientation === "portrait" ? image.originalWidth : image.originalHeight,
            orientation === "portrait" ? image.originalHeight : image.originalWidth,
            image.containerWidth,
            image.containerHeight,
            false,
        ).width;
    };

    const handleReset = async () => {
        if (!rotateRef?.current || !parentImageRef?.current || !image) return;
        dispatch(rotateSetStraighten(0));
        dispatch(rotateSetRotation(0));
        rotateImage(parentImageRef.current, rotateRef.current, image.initialWidth, 0, 0, false);
    };

    const handleSlideEnd = async () => {
        isSlidingRef.current = false;
        if (!rotateRef?.current || !parentImageRef?.current || !image) return;
        const rotation = store.getState().flipRotate.rotate.rotation;
        const straighten = store.getState().flipRotate.rotate.straighten;
        rotateImage(parentImageRef.current, rotateRef.current, rotateRef.current.width, rotation, straighten, false);
    };

    return {
        straighten,
        handleStraightenChange,
        handleDirection,
        handleReset,
        handleSlideEnd,
        isRotating,
    };
};

const equalityFn = (left: FlipRotateState, right: FlipRotateState) => {
    if (
        left.rotate.rotation !== right.rotate.rotation ||
        left.rotate.straighten !== right.rotate.straighten ||
        JSON.stringify(left.image) !== JSON.stringify(right.image)
    )
        return false;

    return true;
};

function drawImage(
    context: CanvasRenderingContext2D,
    image: HTMLImageElement,
    scale: number,
    w: number,
    h: number,
    rotate: number,
    degrees: number,
    enableDrawBoard: boolean,
) {
    context.save();
    context.clearRect(0, 0, w, h);
    context.translate(w / 2, h / 2);

    context.rotate((degrees * Math.PI) / 180); // angle must be in radians

    context.scale(scale, scale);
    if (Math.abs(rotate) === 90 || Math.abs(rotate) === 270) {
        context.drawImage(image, -h / 2, -w / 2, h, w);
    } else {
        context.drawImage(image, -w / 2, -h / 2, w, h);
    }
    context.translate(-w / 2, -h / 2);
    context.restore();

    if (enableDrawBoard) drawBoard(context, w, h);
}

export function drawBoard(context: CanvasRenderingContext2D, w: number, h: number) {
    for (var x = 0; x <= w; x += 25) {
        context.moveTo(x, 0);
        context.lineTo(x, h);
    }

    for (var x = 0; x <= h; x += 25) {
        context.moveTo(0, x);
        context.lineTo(w, x);
    }

    context.strokeStyle = "rgba(240, 240, 240, .5)";
    context.stroke();
}

export const rotateImage = async (
    parentImage: HTMLImageElement,
    image: HTMLImageElement,
    maxWidth: number,
    rotate: number,
    straigten: number,
    enableDrawBoard: boolean,
) => {
    const canvas = document.createElement("canvas");
    let width, height, scale, c, d, e, f;

    let canvasScale;
    if (Math.abs(rotate) === 90 || Math.abs(rotate) === 270) {
        canvasScale = maxWidth / parentImage.height;
        width = maxWidth;
        height = parentImage.width * canvasScale;
        if (height > parentImage.height) {
            canvasScale = parentImage.height / height;
            width = width * canvasScale;
            height = height * canvasScale;
        }
    } else {
        canvasScale = maxWidth / parentImage.width;
        width = maxWidth;
        height = parentImage.height * canvasScale;
    }

    const dpr = window.devicePixelRatio;
    width *= dpr;
    height *= dpr;

    canvas.width = width;
    canvas.height = height;

    const angle = (Math.abs(straigten) * Math.PI) / 180;
    c = Math.cos(angle) * width;
    d = Math.cos(angle) * height;
    e = Math.sin(angle) * height;
    f = Math.sin(angle) * width;
    var ctx = canvas.getContext("2d");

    if (!ctx) return;

    scale = Math.max((d + f) / height, (c + e) / width);

    drawImage(ctx, parentImage, scale, width, height, rotate, rotate + straigten, enableDrawBoard);
    image.width = canvas.width / dpr;
    image.height = canvas.height / dpr;
    await loadSrcToImage(canvas.toDataURL(), image);
};

export const getOrientation = (rotation: number): Orientation =>
    Math.abs(rotation) === 90 || Math.abs(rotation) === 270 ? "landscape" : "portrait";
